<template>
  <div class="instantwin-message" v-loading="isLoadingInstantwinMessages || isLoadingCampaign">
    <div class="mb-x2" layout="layout" layout-align="space-between-center">
      <div layout="layout">
        <div class="mr-x1">
          <el-button class="el-button-mt" type="default" @click="onChangeBackEvent"
            ><i class="mdi mdi-arrow-left"></i>戻る</el-button
          >
        </div>
      </div>
      <div v-if="campaign.id" layout="row" layout-align="end-center">
        <div class="mr-x3">応募者数 : {{ formatNumber(campaign.applicantsCount) }}</div>
        <div class="mr-x3">応募総数 : {{ formatNumber(campaign.entryCount) }}</div>
        <div class="mr-x3">抽選件数 : {{ formatNumber(campaign.lotteryCount) }}</div>
        <div class="mr-x3">
          当選総数 : {{ formatNumber(campaign.maxWinnerCount) }} (残り
          {{ formatNumber(campaign.maxWinnerCount - campaign.winnerCount) }})
        </div>
      </div>
    </div>
    <el-card v-if="!error">
      <div class="mb-x3" layout="row" layout-align="space-between-center">
        <div>
          <el-button
            type="primary"
            @click="openInstantwinMessageDialog"
            :disabled="isCampaignFinished || (!isSystemAdmin && !allowCreateInstantwin)"
            ><i class="mdi mdi-plus"></i>自動配信メッセージの新規作成</el-button
          >
          <el-button type="default" @click="$refs.messageQueueDialog.open()" :disabled="!is_account_exist"
            ><i class="mdi mdi-calendar-clock"></i>一括配信メッセージの管理</el-button
          >
          <el-button type="default" @click="$refs.serialCodeManagement.open(campaign.id)"
            ><i class="mdi mdi-ticket"></i>シリアルコードの管理</el-button
          >
          <el-button
            :disabled="!campaign.id"
            @click="$router.push('/twitter_instantwins/' + $route.params.campaignId + '/message_histories')"
            ><i class="mdi mdi-format-list-bulleted"></i>配信履歴</el-button
          >
        </div>
        <csv-download v-if="!error && messages.length" :download="downloadCsv"></csv-download>
      </div>
      <div class="message message-info" v-if="messages.length === 0 && !isLoadingInstantwinMessages">
        メッセージがまだ作成されていません。インスタントウィンの応募に自動返信するメッセージを作成してください。
      </div>
      <instantwin-message-table
        v-if="messages.length"
        :data="messages"
        :options="options"
        :onChangePagingNo="onChangePagingNo"
        :onChangeLimitCount="onChangeLimitCount"
        :onChangeSortBy="onChangeSortBy"
      ></instantwin-message-table>
      <instantwin-message-dialog ref="instantwinMessageDialog"></instantwin-message-dialog>
      <message-queue-dialog ref="messageQueueDialog"></message-queue-dialog>
      <serial-code-management ref="serialCodeManagement"></serial-code-management>
    </el-card>
    <el-card v-else>
      <div class="message message-error">
        データ取得に失敗しました。恐れ入りますが、時間を置いて再度お試しください。
      </div>
    </el-card>
  </div>
</template>

<script lang="ts">
// @ts-nocheck TODO: 既存の型エラーを解消したら外す
import moment from 'moment'
import { mapActions, mapState } from 'pinia'
import { defineComponent } from 'vue'

import CsvDownload from '@/client/components/_atoms/csv_download'
import { CAMPAIGN_STATUS } from '@/client/constant'
import api from '@/client/core/api'
import util from '@/client/core/util'
import InstantwinMessageDialog from '@/client/features/twitter_instantwin_message/instantwin_message_dialog'
import InstantwinMessageTable from '@/client/features/twitter_instantwin_message/instantwin_message_table'
import MessageQueueDialog from '@/client/features/twitter_instantwin_message/message_queue_dialog'
import SerialCodeManagement from '@/client/features/twitter_instantwin_message/serial_code_management'
import { formatDatetime, formatNumber } from '@/client/filters/filters'
import { useCampaignStore } from '@/client/store/useCampaignStore'
import { useNavbarStore } from '@/client/store/useNavbarStore'
import { useSystemStore } from '@/client/store/useSystemStore'
import { useTwitterInstantwinMessageStore } from '@/client/store/useTwitterInstantwinMessageStore'

export default defineComponent({
  components: {
    CsvDownload,
    InstantwinMessageTable,
    InstantwinMessageDialog,
    MessageQueueDialog,
    SerialCodeManagement
  },
  data: () => {
    return {
      isLeave: false
    }
  },
  computed: {
    ...mapState(useSystemStore, {
      currentGroupId: state => state.currentGroup.id,
      allowCreateInstantwin: state => Boolean(state.currentGroup.allowCreateInstantwin),
      isSystemAdmin: state => Boolean(state.user.isSystemAdmin)
    }),
    ...mapState(useCampaignStore, {
      lastQueryParams: state => state.lastQueryParams
    }),
    ...mapState(useTwitterInstantwinMessageStore, {
      messages: state => state.messages,
      error: state => state.error,
      params: state => state.params,
      campaignInfo: state => state.campaignInfo,
      isLoadingInstantwinMessages: state => state.isLoadingInstantwinMessages,
      isLoadingCampaign: state => state.isLoadingCampaign,
      campaign: state => {
        if (state.campaign) {
          return state.campaign
        }
        return {}
      },
      options: state => {
        return {
          total: state.total,
          pagingNo: state.params.pagingNo,
          limitCount: state.params.limitCount,
          sortBy: state.params.sortBy
        }
      }
    }),
    isCampaignFinished() {
      return [CAMPAIGN_STATUS.DURING_FINAL_COLLECTION, CAMPAIGN_STATUS.COLLECTION_COMPLETED].includes(
        this.campaign.status
      )
    },
    is_account_exist() {
      return typeof this.campaign.account === 'object'
    }
  },
  async mounted() {
    const params = {
      campaignId: this.$route.params.campaignId
    }

    this.setParams(params)

    await this.fetchCampaign()

    if (this.isLeave || !this.campaign || !this.campaign.id) {
      return
    }

    if (this.currentGroupId !== this.campaign.groupId) {
      this.setCurrentGroup(this.campaign.groupId)
    }

    this.setBreadcrumb([
      {
        name: 'キャンペーン一覧',
        link: '/campaigns',
        iconUrl: null,
        iconType: null
      },
      {
        name: this.campaign.title,
        link: null,
        iconName: this.campaign.account ? this.campaign.account.name : '削除されたアカウント',
        iconUrl: this.campaign.account ? this.campaign.account.pictureUrl : null,
        iconType: this.campaign.snsType,
        label: this.campaign.type === 'instantwin' ? 'インスタントウィン' : null,
        isIcon: true
      }
    ])
  },

  watch: {
    params() {
      this.fetchInstantwinMessages()
    }
  },

  methods: {
    formatNumber,

    ...mapActions(useSystemStore, ['setCurrentGroup']),
    ...mapActions(useTwitterInstantwinMessageStore, ['fetchInstantwinMessages', 'setParams', 'fetchCampaign', 'reset']),
    ...mapActions(useNavbarStore, ['setBreadcrumb']),

    /**
     * 戻るボタンクリック時の処理
     */
    onChangeBackEvent() {
      // 保存されたクエリパラメータがあれば復元、なければそのまま
      const query = this.lastQueryParams || {}
      this.$router.push({ path: '/campaigns', query })
    },

    /**
     * ページを変更イベント
     * @param {number} page ページ
     */
    onChangePagingNo(page: number) {
      if (this.params.pagingNo === page) {
        return
      }
      this.setParams({ pagingNo: page })
      util.scrollToTop()
    },

    /**
     * リミット数を変更イベント
     * @param {number} limitCount リミット数
     */
    onChangeLimitCount(limitCount: number) {
      if (this.params.limitCount === limitCount) {
        return
      }
      this.setParams({ limitCount })
    },

    /**
     * ソートを変更イベント
     * @param {string} sortBy ソート
     */
    onChangeSortBy(sortBy: string) {
      if (this.params.sortBy === sortBy) {
        return
      }
      this.setParams({ sortBy })
    },

    /**
     * CSVダウンロード
     */
    async downloadCsv() {
      const params = Object.assign({}, this.params, {
        limitCount: -1
      })
      const result = await api.get('/twitter_instantwin_messages', {
        params
      })

      if (!result.data) {
        return {
          error: 'ERROR_LOADING'
        }
      }

      const messages = result.data.messages
      const filename = `instantwin_messages_${moment().format('YYYY_MM_DD')}`
      const csvData = []
      csvData.push([
        'ステータス',
        '種別',
        'メッセージ名',
        '開始日時',
        '終了日時',
        '1日の切り替え時間',
        '1日1回抽選',
        '当メッセージで当選実績がある場合、落選にする',
        '別メッセージで当選実績がある場合、落選にする',
        'キーワード指定',
        '重み付け値',
        '返信方法',
        '当選率',
        '当選率変動',
        '配信数(本日)',
        '1日の最大当選数',
        '配信数(本日の残り)',
        '配信数',
        '配信数(残り)',
        'メンション内容',
        '添付ファイル名(メンション)',
        'ダイレクトメッセージ内容',
        '添付ファイル名(DM)'
      ])

      // CSVデータを格納
      messages.forEach(message => {
        let status = ''
        switch (message.status) {
          case 2:
            status = 'スケジュール'
            break
          case 1:
            status = 'アクティブ'
            break
          case 0:
            status = '終了'
            break
        }

        let replyType = ''
        switch (message.replyType) {
          case 'mention':
            replyType = 'メンション'
            break
          case 'dm':
            replyType = 'DM'
            break
          case 'mention-and-dm':
            replyType = 'メンション & DM'
            break
        }

        let mentionBody = ''
        let mentionFileName = ''
        let dmBody = ''
        let dmFileName = ''
        message.tw_instantwin_message_contents.forEach(content => {
          switch (content.replyType) {
            case 'mention':
              mentionBody = content.body
              if (content.tw_instantwin_message_content_medias.length) {
                mentionFileName = content.tw_instantwin_message_content_medias[0].mediaOriginName
              }
              break

            case 'dm':
              dmBody = content.body
              if (content.tw_instantwin_message_content_medias.length) {
                dmFileName = content.tw_instantwin_message_content_medias[0].mediaOriginName
              }
              break
          }
        })

        const entryKeyword = message.entryKeyword ? JSON.parse(message.entryKeyword) : []

        let winningRateChangeType = ''

        if (message.winningRateChangeType === 'none') {
          winningRateChangeType = '変動なし'
        } else if (message.winningRateChangeType === 'auto') {
          winningRateChangeType = '自動変動'
        } else if (message.winningRateChangeType === 'time') {
          winningRateChangeType = `配信期間の${message.elapsedRate}割経過時、当選率を${message.elapsedWinningRate}%にする`
        }

        const isDailyLottery = message.isDailyLottery ? 'ON' : 'OFF'
        const excludeOtherMessageMultiWinner = message.excludeOtherMessageMultiWinner ? 'ON' : 'OFF'
        const excludeMessageMultiWinner = message.excludeMessageMultiWinner ? 'ON' : 'OFF'
        const winningRate = message.winningRateChangeType === 'auto' ? '自動変動' : message.winningRate + '%'
        const dailyWinnerCount = message.dailyWinnerCount ? message.dailyWinnerCount : '制限なし'

        csvData.push([
          status,
          message.messageType === 'win' ? '当選' : '落選',
          message.name,
          formatDatetime(message.startDatetime),
          formatDatetime(message.endDatetime),
          message.messageType === 'win' ? message.dailyWinnerChangeTime : '-',
          message.messageType === 'win' ? isDailyLottery : '-',
          message.messageType === 'win' ? excludeOtherMessageMultiWinner : '-',
          message.messageType === 'win' ? excludeMessageMultiWinner : '-',
          entryKeyword.join(','),
          message.weightCount,
          replyType,
          message.messageType === 'win' ? winningRate : '-',
          message.messageType === 'win' ? winningRateChangeType : '-',
          message.messageType === 'win' ? message.dailySendCount : '-',
          message.messageType === 'win' ? dailyWinnerCount : '-',
          message.messageType === 'win' ? message.remainDailySendCount : '-',
          message.sendCount,
          message.messageType === 'win' ? message.remainSendCount : '-',
          mentionBody ? mentionBody.replace(/(\r\n|\n|\r)/gm, '') : '',
          mentionFileName,
          dmBody ? dmBody.replace(/(\r\n|\n|\r)/gm, '') : '',
          dmFileName
        ])
      })
      return {
        filename,
        data: csvData
      }
    },

    /**
     * ダイアログを開く
     */
    openInstantwinMessageDialog() {
      this.$refs.instantwinMessageDialog.open()
    }
  },

  beforeRouteLeave(to: any, from: any, next: any) {
    // 応募者ストアをリセット
    this.reset()
    this.isLeave = true
    next()
  }
})
</script>
